import { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  useAddVendorMutation,
  useUpdateVendorMutation,
  useGetVendorCategoriesQuery,
  useAddVendorCategoryMutation,
} from "../../../services/vendor";

interface VendorFormValues { name: string; mobile: string; categoryId: string; }

interface AddVendorModalProps {
  visible:      boolean;
  onCancel:     () => void;
  onSuccess?:   (vendor: { _id?: string; name?: string; mobile?: string }) => void;
  // When set, the modal edits this vendor instead of creating a new one.
  // `category` here is the vendor's current category *name* (that's all the
  // vendor list response carries) — resolved to its _id via categoryList
  // below once categories have loaded.
  vendor?:      { _id: string; name?: string; mobile?: string; category?: string } | null;
  // Display-only override — the underlying data/API is still Vendor either
  // way (Add Expense calls this "Merchant" wording-wise, but still creates a
  // real Vendor record since that's what expense transactions reference).
  entityLabel?: string;
}

const AddVendorModal = ({ visible, onCancel, onSuccess, vendor, entityLabel = 'Vendor' }: AddVendorModalProps) => {
  const [form] = Form.useForm<VendorFormValues>();
  const isEdit = !!vendor?._id;
  const [addVendor, { isLoading: adding }]     = useAddVendorMutation();
  const [updateVendor, { isLoading: updating }] = useUpdateVendorMutation();
  const submitting = adding || updating;

  const { data: categoriesResp, isFetching: categoriesLoading } = useGetVendorCategoriesQuery();
  const [addVendorCategory, { isLoading: addingCategory }] = useAddVendorCategoryMutation();
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const categoryList = Array.isArray(categoriesResp)
    ? categoriesResp
    : (Array.isArray((categoriesResp as any)?.data) ? (categoriesResp as any).data : []);
  const categoryOptions = categoryList.map((c: any) => ({ value: c._id, label: c.name }));

  useEffect(() => {
    if (!visible) return;
    const matchedCategory = vendor?.category
      ? categoryList.find((c: any) => c.name?.toLowerCase() === vendor.category?.toLowerCase())
      : null;
    form.setFieldsValue({
      name:       vendor?.name || '',
      mobile:     vendor?.mobile || '',
      categoryId: matchedCategory?._id || '',
    });
  }, [visible, vendor, categoryList, form]);

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async (values: VendorFormValues) => {
    try {
      if (isEdit) {
        await updateVendor({ id: vendor!._id, ...values }).unwrap();
        // message.success('Vendor updated successfully');
        onSuccess?.({ _id: vendor!._id, ...values });
      } else {
        const res = await addVendor(values).unwrap();
        // message.success('Vendor added successfully');
        onSuccess?.((res as any)?.data ?? values);
      }
      handleClose();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const res = await addVendorCategory({ name }).unwrap();
      const newId = (res as any)?.data?._id ?? (res as any)?._id;
      // message.success('Category added successfully');
      if (newId) form.setFieldsValue({ categoryId: newId });
      setNewCategoryName('');
      setAddCategoryOpen(false);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      title={isEdit ? `Edit ${entityLabel}` : `Add ${entityLabel}`}
      okText={isEdit ? 'Save' : 'Add'}
      onOk={() => form.submit()}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="name" label={`${entityLabel} Name`} rules={[{ required: true, message: `Please enter ${entityLabel.toLowerCase()} name` }]}>
          <Input placeholder={`Enter ${entityLabel.toLowerCase()} name`} />
        </Form.Item>

        <Form.Item
          name="mobile"
          label="Mobile Number"
          rules={[
            { required: true, message: 'Please enter mobile number' },
            { pattern: /^\d{10}$/, message: 'Enter a valid 10-digit mobile number' },
          ]}
        >
          <Input placeholder="Enter mobile number" maxLength={10} />
        </Form.Item>

        <Form.Item name="categoryId" label="Category" rules={[{ required: true, message: 'Please select a category' }]}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              placeholder="Select category"
              loading={categoriesLoading}
              showSearch
              optionFilterProp="label"
              options={categoryOptions}
              style={{ flex: 1 }}
              value={Form.useWatch('categoryId', form)}
              onChange={(v) => form.setFieldsValue({ categoryId: v })}
            />
            <Button icon={<PlusOutlined />} onClick={() => setAddCategoryOpen(true)} />
          </div>
        </Form.Item>
      </Form>

      <Modal
        title="Add Category"
        open={addCategoryOpen}
        onCancel={() => { setAddCategoryOpen(false); setNewCategoryName(''); }}
        onOk={handleAddCategory}
        okText="Add"
        confirmLoading={addingCategory}
        destroyOnClose
      >
        <Input
          placeholder="e.g. Rent"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          onPressEnter={handleAddCategory}
        />
      </Modal>
    </Modal>
  );
};

export default AddVendorModal;
